// --- SaniQ ---
import {deserializeItem} from '../index';
import {deserializeGroup, Group} from "./Group";
import {parseOptionalISO} from "helpers/Parser";
import {
  DeserializedRecord,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "../Helpers";
import {FeelingFactor} from "../../modules/system/modules/feeling_factors/types";
import {MeasurementKey} from 'vendor/qurasoft/definitions/types/measurements';

interface MeasurementFeelingFactor extends DeserializedRecord {
  feelingFactor: FeelingFactor;
  value: 'nothing' | 'light' | 'medium' | 'high';
}

interface Weather extends DeserializedRecord {
  icon: string;
}

interface Measurement extends DeserializedRecord<"measurement"> {
  bodyLocation?: string;
  feelingFactors?: MeasurementFeelingFactor[];
  group?: Group;
  location?: DeserializedRecord;
  measuredAt: Date;
  remark?: string;
  seenBy?: DeserializedRecord;
  source?: string;
  value: number;
  valueType: MeasurementKey;
  weather?: Weather;
  from?: Date;
  to?: Date;
}


interface MeasurementFeelingFactorData extends JSONAPIRecord {
  relationships: {
    feeling_factor: JSONAPISingleRelationship
  }
}

interface MeasurementData extends JSONAPIRecord {
  type: "measurement";
  relationships: {
    location: JSONAPISingleRelationship;
    patient_measurement_feeling_factors: JSONAPIManyRelationship;
    patient_measurement_group: JSONAPISingleRelationship;
    seen_by: JSONAPISingleRelationship;
    weather: JSONAPISingleRelationship;
  }
}

const deserializeMeasurementFeelingFactor: Deserializer<MeasurementFeelingFactor, MeasurementFeelingFactorData> = (data, included) => {
  const feelingFactor = deserializeSingleRelationship<FeelingFactor>(data.relationships.feeling_factor, included);

  return {
    ...deserializeItem(data),
    feelingFactor
  };
};

const deserializeMeasurement: Deserializer<Measurement, MeasurementData> = (data, included) => {
  const id: string = (data.attributes.id || data.id || crypto.randomUUID()) as string;

  //NOTE(Keune): Relationships may not exist since this deserializer also is responsible for the HRV analysis response
  const feelingFactors = deserializeManyRelationship(
    data.relationships?.patient_measurement_feeling_factors,
    included,
    deserializeMeasurementFeelingFactor);

  const group = deserializeSingleRelationship(data.relationships?.patient_measurement_group, included, deserializeGroup);
  const location = deserializeSingleRelationship(data.relationships?.location, included);
  const seenBy = deserializeSingleRelationship(data.relationships?.seen_by, included);
  const weather = deserializeSingleRelationship<Weather>(data.relationships?.weather, included);

  return {
    ...deserializeItem(data),
    ...data,
    id,
    feelingFactors,
    group,
    location,
    seenBy,
    weather,
    // NOTE(Keune): from and to are attributes of HRV analysis
    from: parseOptionalISO(data.attributes.from),
    to: parseOptionalISO(data.attributes.to)
  };
};

export {
  deserializeMeasurement,
  Measurement,
  MeasurementFeelingFactor
};
